import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { Bold, Italic, Underline, List, Palette, Eraser } from "lucide-react";

/**
 * Minimal rich-text editor for plain prose with limited formatting.
 *
 * Supports: bold, italic, underline, colour, bullet list, clear-formatting.
 * Outputs HTML using legacy tags (`<b>`, `<i>`, `<u>`, `<font color>`, `<ul>/<li>`)
 * so the same string can be:
 *  - rendered by RichTextView in the React UI, AND
 *  - converted to ReportLab Paragraph markup server-side.
 *
 * NOTE: uses `document.execCommand` which is technically deprecated but
 * universally supported and the only zero-dep way to do this. Once it's
 * actually removed from browsers we'll swap to a proper editor library.
 */
const COLOR_OPTIONS = [
  { value: "#0A0A0A", label: "Nero" },
  { value: "#FF3B30", label: "Rosso" },
  { value: "#FF5A00", label: "Arancione" },
  { value: "#10b981", label: "Verde" },
];

export default forwardRef(function RichTextEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  "data-testid": testid,
}, ref) {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [bubble, setBubble] = useState(null); // { left, top } or null

  // Sync controlled value → DOM only when content differs (avoid caret reset)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const current = el.innerHTML;
    const next = value || "";
    if (current !== next && document.activeElement !== el) {
      el.innerHTML = next;
    }
  }, [value]);

  // Track selection inside the editor and position the floating bubble toolbar
  // so the user can apply Bold / Italic / Color directly on the selected text
  // without scrolling back up to the top toolbar.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return undefined;

    const compute = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setBubble(null);
        return;
      }
      const range = sel.getRangeAt(0);
      // Only show when the selection is inside this editor instance
      if (!el.contains(range.commonAncestorContainer)) {
        setBubble(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        setBubble(null);
        return;
      }
      const BUBBLE_W = 280;
      const left = Math.max(8, Math.min(window.innerWidth - BUBBLE_W - 8, rect.left + rect.width / 2 - BUBBLE_W / 2));
      // 10 px gap above the selection; clamp to viewport top
      const top = Math.max(8, rect.top - 48);
      setBubble({ left, top });
    };

    const onSelChange = () => compute();
    const onScroll = () => setBubble(null);
    document.addEventListener("selectionchange", onSelChange);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("selectionchange", onSelChange);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const emit = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const exec = (cmd, arg) => {
    // Force legacy tags so the output is friendly for ReportLab (`<b>` not `<strong>` etc.)
    try { document.execCommand("styleWithCSS", false, false); } catch { /* ignore */ }
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
    emit();
  };

  const onInput = () => emit();
  const onPaste = (e) => {
    // Force plain text paste to avoid pasting heavy styles
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  // Track caret so external pickers (e.g. ConclusioniPhrasePicker) can
  // restore the cursor position after a modal steals focus.
  const onSelectionPersist = () => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    saveSelection: () => onSelectionPersist(),
    /**
     * Insert HTML at the previously-saved caret. If no saved caret exists or
     * it has been detached from the DOM, append at the end of the editor.
     */
    insertHTMLAtSavedCursor: (html) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      let saved = savedRangeRef.current;
      const validSaved =
        saved &&
        el.contains(saved.startContainer) &&
        el.contains(saved.endContainer);
      if (validSaved) {
        sel.removeAllRanges();
        sel.addRange(saved);
      } else {
        // Place caret at end of editor content
        const r = document.createRange();
        r.selectNodeContents(el);
        r.collapse(false);
        sel.removeAllRanges();
        sel.addRange(r);
      }
      try { document.execCommand("styleWithCSS", false, false); } catch { /* ignore */ }
      document.execCommand("insertHTML", false, html);
      emit();
      // Update saved range to the new caret position (end of inserted block)
      const newSel = window.getSelection();
      if (newSel && newSel.rangeCount > 0) {
        savedRangeRef.current = newSel.getRangeAt(0).cloneRange();
      }
    },
  }));

  const btnCls = "h-8 w-8 rounded-md border border-transparent hover:border-black hover:bg-white flex items-center justify-center transition";

  return (
    <div className="bru-input !p-0 overflow-hidden" data-testid={testid}>
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-neutral-200 bg-neutral-50/95 backdrop-blur-sm">
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec("bold"); }}
          className={btnCls}
          title="Grassetto (Ctrl+B)"
          data-testid={testid ? `${testid}-bold` : undefined}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec("italic"); }}
          className={btnCls}
          title="Corsivo (Ctrl+I)"
          data-testid={testid ? `${testid}-italic` : undefined}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec("underline"); }}
          className={btnCls}
          title="Sottolineato (Ctrl+U)"
          data-testid={testid ? `${testid}-underline` : undefined}
        >
          <Underline className="w-4 h-4" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }}
          className={btnCls}
          title="Elenco a punti"
          data-testid={testid ? `${testid}-bullet` : undefined}
        >
          <List className="w-4 h-4" />
        </button>
        <div className="h-5 w-px bg-neutral-300 mx-1" />
        <div className="relative flex items-center gap-1" title="Colore">
          <Palette className="w-3.5 h-3.5 text-neutral-500" />
          {COLOR_OPTIONS.map((c) => (
            <button
              key={c.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); exec("foreColor", c.value); }}
              className="w-5 h-5 rounded-full border border-neutral-300 hover:scale-110 transition-transform"
              style={{ background: c.value }}
              title={c.label}
              aria-label={`Colore ${c.label}`}
              data-testid={testid ? `${testid}-color-${c.value.replace("#", "")}` : undefined}
            />
          ))}
        </div>
        <div className="h-5 w-px bg-neutral-300 mx-1" />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            // First, exit any active list (insertUnorderedList toggles, so only
            // call when currently inside a list — otherwise it would ADD one).
            try {
              if (document.queryCommandState && document.queryCommandState("insertUnorderedList")) {
                document.execCommand("insertUnorderedList", false);
              }
              if (document.queryCommandState && document.queryCommandState("insertOrderedList")) {
                document.execCommand("insertOrderedList", false);
              }
            } catch { /* ignore */ }
            exec("removeFormat");
          }}
          className={btnCls}
          title="Rimuovi formattazione"
          data-testid={testid ? `${testid}-clear` : undefined}
        >
          <Eraser className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onBlur={() => { onSelectionPersist(); emit(); }}
        onKeyUp={onSelectionPersist}
        onMouseUp={onSelectionPersist}
        onPaste={onPaste}
        className="rte-content px-3 py-2 text-sm focus:outline-none whitespace-pre-wrap break-words overflow-y-auto"
        style={{ minHeight: `${Math.max(40, rows * 22)}px`, maxHeight: "40vh" }}
        data-placeholder={placeholder}
      />
      <style>{`
        .rte-content[contenteditable]:empty::before {
          content: attr(data-placeholder);
          color: rgb(163 163 163);
          pointer-events: none;
        }
        .rte-content ul { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; }
        .rte-content ol { list-style: decimal; padding-left: 1.25rem; margin: 0.25rem 0; }
        .rte-content li { margin: 0.15rem 0; }
      `}</style>
      {bubble && typeof document !== "undefined" && createPortal(
        <div
          className="fixed z-[60] flex items-center gap-0.5 px-1 py-1 rounded-lg border-[1.5px] border-black bg-white shadow-lg"
          style={{ left: bubble.left, top: bubble.top, width: 280 }}
          onMouseDown={(e) => e.preventDefault()}
          data-testid={testid ? `${testid}-bubble` : undefined}
        >
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} className={btnCls} title="Grassetto">
            <Bold className="w-4 h-4" />
          </button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} className={btnCls} title="Corsivo">
            <Italic className="w-4 h-4" />
          </button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("underline"); }} className={btnCls} title="Sottolineato">
            <Underline className="w-4 h-4" />
          </button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} className={btnCls} title="Elenco">
            <List className="w-4 h-4" />
          </button>
          <div className="h-5 w-px bg-neutral-300 mx-1" />
          {COLOR_OPTIONS.map((c) => (
            <button
              key={c.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); exec("foreColor", c.value); }}
              className="w-5 h-5 rounded-full border border-neutral-300 hover:scale-110 transition-transform"
              style={{ background: c.value }}
              title={c.label}
              aria-label={`Colore ${c.label}`}
            />
          ))}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              try {
                if (document.queryCommandState && document.queryCommandState("insertUnorderedList")) {
                  document.execCommand("insertUnorderedList", false);
                }
              } catch { /* ignore */ }
              exec("removeFormat");
            }}
            className={btnCls}
            title="Rimuovi formattazione"
          >
            <Eraser className="w-4 h-4" />
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
});

/**
 * Read-only renderer for RichTextEditor output. Sanitises against the same
 * whitelist used by the backend so injected scripts/styles can't sneak in.
 */
export function RichTextView({ html, className = "", "data-testid": testid }) {
  const safe = sanitizeRichTextHtml(html || "");
  if (!safe) return null;
  return (
    <div
      className={`rte-content text-sm whitespace-pre-wrap break-words ${className}`}
      data-testid={testid}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

/**
 * Whitelist sanitiser. Keeps only known-safe tags/attributes — strips scripts,
 * styles, event handlers and arbitrary classes.
 * Allowed: b, strong, i, em, u, br, p, ul, ol, li, font(color), span(style=color only)
 */
export function sanitizeRichTextHtml(html) {
  if (!html) return "";
  if (typeof window === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstChild;
    if (!root) return "";

    const ALLOWED_TAGS = new Set([
      "B", "STRONG", "I", "EM", "U", "BR", "P", "UL", "OL", "LI", "FONT", "SPAN", "DIV",
    ]);
    const COLOR_RE = /^#?[0-9a-fA-F]{3,6}$|^rgb\([^)]+\)$|^rgba\([^)]+\)$|^[a-z]+$/;

    const walk = (node) => {
      // iterate over a static copy because we mutate the DOM
      const children = Array.from(node.childNodes);
      for (const child of children) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          if (!ALLOWED_TAGS.has(child.tagName)) {
            // unwrap (replace with its children)
            while (child.firstChild) node.insertBefore(child.firstChild, child);
            node.removeChild(child);
            continue;
          }
          // strip attributes except known-safe ones
          const keepAttrs = new Map();
          if (child.tagName === "FONT") {
            const color = child.getAttribute("color");
            if (color && COLOR_RE.test(color)) keepAttrs.set("color", color);
          } else if (child.tagName === "SPAN") {
            const style = child.getAttribute("style") || "";
            const m = /color\s*:\s*([^;]+)/i.exec(style);
            if (m && COLOR_RE.test(m[1].trim())) {
              keepAttrs.set("style", `color: ${m[1].trim()}`);
            }
          }
          // remove all attributes
          while (child.attributes.length > 0) {
            child.removeAttribute(child.attributes[0].name);
          }
          // re-add kept ones
          for (const [k, v] of keepAttrs) child.setAttribute(k, v);
          // recurse
          walk(child);
        } else if (child.nodeType !== Node.TEXT_NODE) {
          // remove comments / processing instructions
          node.removeChild(child);
        }
      }
    };
    walk(root);
    return root.innerHTML;
  } catch {
    return "";
  }
}
